import axios from "axios";
import { toast } from "sonner";
import {
  unlockAudio as _unlockAudio,
  isAudioUnlocked as _isAudioUnlocked,
  playUrl as _playUrl,
  preloadB64 as _preloadB64,
  clearCachedAudio as _clearCachedAudio,
  stop as _stop,
  pause as _pause,
  resume as _resume,
  playChime as _playChime,
} from "./audioPlayer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("bb_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem("bb_token");
      localStorage.removeItem("bb_user");
      if (!window.location.pathname.startsWith("/login") && !window.location.pathname.startsWith("/register")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const saveAuth = (token, user) => {
  localStorage.setItem("bb_token", token);
  localStorage.setItem("bb_user", JSON.stringify(user));
};

export const getUser = () => {
  try { return JSON.parse(localStorage.getItem("bb_user") || "null"); } catch { return null; }
};

export const clearAuth = () => {
  localStorage.removeItem("bb_token");
  localStorage.removeItem("bb_user");
};

// Read the current user's repeated-letter spelling preference from the
// cached profile in localStorage. Returns one of:
//   "spell_each"  → say each letter individually (default, legacy behaviour)
//   "double"      → say "Double X" for two consecutive identical letters
// Profile.jsx writes the fresh user object back to localStorage via
// saveAuth(), so this picks up changes on the next spellOut() call.
export const REPEATED_STYLE_SPELL_EACH = "spell_each";
export const REPEATED_STYLE_DOUBLE = "double";
export const getRepeatedLetterStyle = () => {
  try {
    const u = JSON.parse(localStorage.getItem("bb_user") || "null");
    return u?.repeated_letter_style === REPEATED_STYLE_DOUBLE
      ? REPEATED_STYLE_DOUBLE
      : REPEATED_STYLE_SPELL_EACH;
  } catch {
    return REPEATED_STYLE_SPELL_EACH;
  }
};

// ---------- Speech ----------
const browserSpeak = (text, speed = 0.85) =>
  new Promise((resolve) => {
    try {
      if (typeof window === "undefined" || !window.speechSynthesis) return resolve(false);
      const synth = window.speechSynthesis;
      synth.cancel();
      const utter = () => {
        const u = new SpeechSynthesisUtterance(text);
        u.rate = speed;
        u.pitch = 1.05;
        u.volume = 1;
        const voices = synth.getVoices() || [];
        const preferred =
          voices.find(v => /en(-|_)?(US|GB)/i.test(v.lang) && /female|samantha|google|zira|karen|moira|tessa/i.test(v.name)) ||
          voices.find(v => /^en/i.test(v.lang)) ||
          voices[0];
        if (preferred) u.voice = preferred;
        let done = false;
        const finish = (ok) => { if (!done) { done = true; resolve(ok); } };
        u.onend = () => finish(true);
        u.onerror = () => finish(false);
        try { synth.speak(u); } catch { return finish(false); }
        // Treat 'started' as success: some browsers never fire onend for short utterances
        setTimeout(() => finish(true), Math.max(1500, text.length * 90));
      };
      // voices may not be loaded yet (Chrome async load)
      if (!synth.getVoices().length) {
        const onVoices = () => { synth.removeEventListener("voiceschanged", onVoices); utter(); };
        synth.addEventListener("voiceschanged", onVoices);
        // fallback if voiceschanged never fires
        setTimeout(utter, 400);
      } else {
        utter();
      }
    } catch { resolve(false); }
  });

let _budgetWarned = false;

// Two distinct voices used for the practice/test "hear it twice" feature.
// Nova (warm female) → Onyx (deeper male) gives ESL ears a clear contrast
// of the same word in two timbres.
export const WORD_VOICE_PRIMARY = "nova";
export const WORD_VOICE_SECONDARY = "onyx";

// ---------- HTML5 audio plumbing ----------
//
// Why HTMLAudioElement (not Web Audio API):
// On iOS, audio routed through `AudioContext` is treated as *ambient*
// sound and gets MUTED by the hardware Silent / Ring switch. Apps like
// YouTube don't mute in Silent Mode because they play through `<audio>` /
// HTMLMediaElement, which iOS routes through the **media** audio session.
//
// `audioPlayer.js` owns the shared HTMLAudioElement + one-time iOS unlock
// (a silent.mp3 played inside the first user gesture). Per-payload blob
// URLs are cached so repeated playback is instant.
//
// Public surface kept identical to the old Web-Audio implementation so
// existing pages (Listen / Practice / Test / WordListDetail / Admin)
// don't need any refactor.

// Cache base64 audio payloads → blob URLs. The map only stores keys;
// the actual blob URL lives inside audioPlayer's internal cache so all
// callers share the same one.
const _wordKeys = new Set();   // "word|voice"
const _letterKeys = new Set(); // "L:A" etc.
const _doubleWordCached = { ok: false }; // shared standalone "Double." clip

const _wordKey = (word, voice) => `w:${word}|${voice}`;
const _letterKey = (L) => `L:${L}`;
const _DOUBLE_WORD_KEY = "DW:double";

const fetchTtsB64 = async (text, voice, speed) => {
  const { data } = await api.post("/tts", { text, voice, speed });
  return { b64: data.audio_base64, mime: data.mime || "audio/mpeg" };
};

const fetchWordB64 = async (word, voice = WORD_VOICE_PRIMARY) => {
  const { data } = await api.get("/words/audio", { params: { word, voice } });
  return { b64: data.audio_base64, mime: data.mime || "audio/mpeg" };
};

// Resolve to a cached blob URL for the given word+voice. Fetches the
// base64 audio from the backend on the first call and pins the blob URL
// for the rest of the page lifetime.
const ensureWordUrl = async (word, voice) => {
  const key = _wordKey(word, voice);
  if (_wordKeys.has(key)) {
    // audioPlayer caches by key — preloadB64 with no payload is cheap
    // miss; we know it's there because we set it on first fetch.
    const cached = _preloadB64(key, null);
    if (cached) return cached;
  }
  const { b64, mime } = await fetchWordB64(word, voice);
  const url = _preloadB64(key, b64, mime);
  if (url) _wordKeys.add(key);
  return url;
};

const ensureLetterUrl = async (L) => {
  const key = _letterKey(L);
  if (_letterKeys.has(key)) {
    const cached = _preloadB64(key, null);
    if (cached) return cached;
  }
  const { b64, mime } = await fetchTtsB64(L, "nova", 0.9);
  const url = _preloadB64(key, b64, mime);
  if (url) _letterKeys.add(key);
  return url;
};

// Resolve a cached blob URL for the spoken word "Double." — used by
// spellOut when the user's repeated_letter_style preference is "double".
//
// Why a standalone "Double." clip (and NOT a combined "Double X" clip):
// OpenAI tts-1-hd compresses the phrase "Double P" to ~620ms, with the
// "Double" syllable getting swallowed by labial stops like /p/, /b/, /t/.
// Variants ("Double, P.", "Double... P." at 0.85 speed) only gained ~25-50ms
// — perceptually too subtle. By contrast, playing "Double." (624ms) as its
// own clip followed immediately by the existing single-letter clip ("P.",
// 552ms) gives kids two clearly-articulated words back-to-back. Bonus: only
// ONE shared "Double." clip is ever fetched (vs 26 per-letter variants),
// cutting TTS cost dramatically.
const ensureDoubleWordUrl = async () => {
  if (_doubleWordCached.ok) {
    const cached = _preloadB64(_DOUBLE_WORD_KEY, null);
    if (cached) return cached;
  }
  const { b64, mime } = await fetchTtsB64("Double.", "nova", 0.9);
  const url = _preloadB64(_DOUBLE_WORD_KEY, b64, mime);
  if (url) _doubleWordCached.ok = true;
  return url;
};

/**
 * Tokenize a word's letters for spellOut playback.
 *
 * @param {string[]} letters  UPPERCASE A-Z letters of the word
 * @param {"spell_each"|"double"} style
 * @returns {{ type: "single"|"double", letter: string, indices: number[] }[]}
 *
 * Greedy left-to-right pair matching, so exactly TWO consecutive identical
 * letters merge into one "double" token. Three repeats produce
 * [double, single] (e.g. "aaa" → "Double A" + "A"), four repeats produce
 * two doubles ("aaaa" → "Double A" + "Double A"), matching the product spec.
 */
const tokenizeForSpell = (letters, style) => {
  const tokens = [];
  const wantDouble = style === REPEATED_STYLE_DOUBLE;
  let i = 0;
  while (i < letters.length) {
    if (wantDouble && i + 1 < letters.length && letters[i] === letters[i + 1]) {
      tokens.push({ type: "double", letter: letters[i], indices: [i, i + 1] });
      i += 2;
    } else {
      tokens.push({ type: "single", letter: letters[i], indices: [i] });
      i += 1;
    }
  }
  return tokens;
};

// ---- Public audio control re-exports ------------------------------------

/** Call inside the first user-gesture handler. Idempotent. */
export const unlockAudioPlayback = () => { _unlockAudio(); };

/** Tells whether audio has been unlocked yet (mostly for tests/debug). */
export const isAudioUnlocked = _isAudioUnlocked;

/** Pause the current main playback (does not clear the queue). */
export const pauseCurrentAudio = () => { _pause(); };

/** Resume the previously-paused main playback. */
export const resumeCurrentAudio = () => { _resume(); };

/** Stop the current main playback immediately and resolve its promise. */
export const interruptCurrentAudio = () => { _stop(); };

/**
 * Evict the in-memory cache entry/entries for a word so the next play
 * fetches fresh bytes from the backend.
 */
export const clearWordAudioCache = (word, voice) => {
  const w = String(word || "").toLowerCase();
  if (!w) return;
  if (voice) {
    const k = _wordKey(w, voice);
    _wordKeys.delete(k);
    _clearCachedAudio(k);
    return;
  }
  // Strip every voice entry for this word.
  for (const k of Array.from(_wordKeys)) {
    if (k.startsWith(`w:${w}|`)) _wordKeys.delete(k);
  }
  _clearCachedAudio(`w:${w}|`);
};

// ---- Word playback -----------------------------------------------------

/**
 * Play a spelling-bee word using the server's cached MP3 (preferred).
 * Falls back to browser SpeechSynthesis if the network/cache path fails.
 */
export const playWord = async (word, voice = WORD_VOICE_PRIMARY) => {
  const w = String(word || "").toLowerCase();
  if (!w) return "noop";
  let firstErr;
  try {
    const url = await ensureWordUrl(w, voice);
    if (!url) throw new Error("no blob url");
    await _playUrl(url);
    return "cache";
  } catch (err) {
    firstErr = err;
    console.warn("[playWord] html-audio path failed for", w, voice, err);
  }
  const ok = await browserSpeak(w, 0.85);
  if (ok) return "browser";
  throw firstErr || new Error("Audio playback failed");
};

// Play the word twice using two distinct voices so non-native speakers
// hear the same word with two different timbres / accents. The second
// voice is fetched in parallel with the first one playing so there's
// no awkward gap waiting on the network.
//
// Pass `shouldStop` to bail out cleanly between voices (used by Listen mode
// for instant Skip Next / Skip Prev).
export const playWordTwice = async (word, gapMs = 450, opts = {}) => {
  const { shouldStop } = opts;
  const w = String(word || "").toLowerCase();
  if (!w) return;
  // Kick off the secondary fetch in parallel with the first playback.
  const secondaryPromise = ensureWordUrl(w, WORD_VOICE_SECONDARY).catch((err) => {
    console.warn("[playWordTwice] secondary fetch failed", err);
    return null;
  });
  await playWord(w, WORD_VOICE_PRIMARY);
  if (shouldStop?.()) return;
  const start = Date.now();
  while (Date.now() - start < gapMs) {
    if (shouldStop?.()) return;
    await new Promise((r) => setTimeout(r, Math.min(60, gapMs)));
  }
  if (shouldStop?.()) return;
  const secondaryUrl = await secondaryPromise;
  if (shouldStop?.()) return;
  if (secondaryUrl) {
    try { await _playUrl(secondaryUrl); return; }
    catch (err) { console.warn("[playWordTwice] secondary play failed", err); }
  }
  if (shouldStop?.()) return;
  // Fall back to repeating the primary voice if the secondary couldn't load.
  await playWord(w, WORD_VOICE_PRIMARY);
};

// ---------- Feedback chimes (HTMLAudio MP3 files, no credits) ----------
//
// We ship two tiny pre-generated mp3 files (~5KB each) at /public/sfx so
// chimes play through the iOS *media* audio session, bypassing Silent
// Mode in the same way word playback does.

// Cheerful two-note "ding" for correct answers.
export const playCorrectChime = () => { _playChime("/sfx/correct.mp3"); };

// Soft descending "boop" for wrong answers.
export const playWrongBuzz = () => { _playChime("/sfx/wrong.mp3"); };


export const speak = async (text, voice = "nova", speed = 0.85) => {
  // Try server TTS (natural voice) first
  try {
    const { b64, mime } = await fetchTtsB64(text, voice, speed);
    const url = b64 ? _preloadB64(`tts:${voice}:${speed}:${text}`, b64, mime) : null;
    if (!url) throw new Error("no blob url");
    await _playUrl(url);
    return "server";
  } catch (err) {
    const status = err?.response?.status;
    if (status === 402 && !_budgetWarned) {
      _budgetWarned = true;
      toast.warning("Using offline voice", {
        description: "Top up your Universal Key (Profile → Universal Key → Add Balance) to bring the buzzy cloud voice back.",
        duration: 6000,
      });
    }
    // Fall back to the browser's built-in Speech Synthesis
    const ok = await browserSpeak(text, speed);
    if (!ok) throw err;
    return "browser";
  }
};

// Play a single letter's pronunciation. Uses the shared letter cache so
// subsequent taps are instant and credit-free.
export const playLetter = async (letter) => {
  const L = String(letter || "").toUpperCase();
  if (!/^[A-Z]$/.test(L)) return "noop";
  try {
    const url = await ensureLetterUrl(L);
    if (!url) throw new Error("no blob url");
    await _playUrl(url);
    return "server";
  } catch (err) {
    const status = err?.response?.status;
    if (status === 402 && !_budgetWarned) {
      _budgetWarned = true;
      toast.warning("Using offline voice", {
        description: "Top up the Universal Key to bring the buzzy cloud voice back.",
        duration: 6000,
      });
    }
    await browserSpeak(L, 0.9);
    return "browser";
  }
};

// Preload pronunciations for all A-Z letters in parallel, so later spellings
// are instant and consistent (no missing-letter glitches during playback).
export const preloadLetters = async () => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  await Promise.all(letters.map(async (L) => {
    if (_letterKeys.has(_letterKey(L))) return;
    try {
      await ensureLetterUrl(L);
    } catch (err) {
      console.warn("[preloadLetters] could not fetch", L, err);
    }
  }));
};

/**
 * Spell a word letter-by-letter aloud, then say the full word.
 *
 * Honours the user's `repeated_letter_style` profile setting:
 *   - "spell_each" (default): every letter pronounced individually
 *     ("A, P, P, L, E")
 *   - "double": two consecutive identical letters merge into a single
 *     "Double X" clip ("A, Double P, L, E"). For longer runs the spec is
 *     greedy: AAA → "Double A, A", AAAA → "Double A, Double A".
 *
 * @param {string} word
 * @param {{ onLetter?: (idx: number, letter: string) => void, speed?: number, shouldStop?: () => boolean, skipFinalWord?: boolean }} opts
 *   onLetter fires as each letter becomes "current"; called with idx=-1 when
 *   the whole spell-out finishes. For "double" tokens the callback fires
 *   TWICE — once at audio start (first index) and once at ~midpoint (second
 *   index) so the on-screen highlight scrolls smoothly across both letters
 *   while a single "Double X" clip plays.
 *   shouldStop is polled between letters; returning true bails out early.
 *   skipFinalWord: when true, don't play the full word at the end (used by
 *   Listen mode which re-plays the word in the next phase anyway).
 */
export const spellOut = async (word, opts = {}) => {
  const { onLetter, speed = 0.9, shouldStop, skipFinalWord = false } = typeof opts === "number" ? { speed: opts } : opts;
  const letters = (word || "").toUpperCase().split("").filter((c) => /[A-Z]/.test(c));
  if (!letters.length) return "noop";
  const style = getRepeatedLetterStyle();
  const tokens = tokenizeForSpell(letters, style);
  // Prefetch every clip we'll need (singles + the shared "Double." clip
  // when style=double) in parallel so playback is gap-free. Mixed
  // failures fall back per-token.
  const needsDoubleWord = tokens.some((t) => t.type === "double");
  let prefetchFailed = false;
  try {
    const tasks = tokens.map((tk) => ensureLetterUrl(tk.letter));
    if (needsDoubleWord) tasks.push(ensureDoubleWordUrl());
    await Promise.all(tasks);
  } catch (err) {
    prefetchFailed = true;
    console.warn("[spellOut] prefetch failed, will fall back per-token:", err);
  }

  // Inter-clip gap inside a "double" token. Long enough that the kid
  // clearly hears two separate words ("Double" then "P"), short enough
  // that they perceive them as a single concept.
  const DOUBLE_INNER_GAP_MS = 80;
  const INTER_TOKEN_GAP_MS = 180;

  const playSingle = async (tk) => {
    onLetter?.(tk.indices[0], tk.letter);
    try {
      const url = !prefetchFailed ? _preloadB64(_letterKey(tk.letter), null) : null;
      if (url) await _playUrl(url);
      else await browserSpeak(tk.letter, 0.85);
    } catch {
      await browserSpeak(tk.letter, 0.85);
    }
  };

  const playDouble = async (tk) => {
    // Phase 1: play "Double." while highlighting the first letter.
    // Splitting the phrase into two clips ("Double." + single letter)
    // sidesteps tts-1-hd's tendency to swallow "Double" before labial
    // stops — see ensureDoubleWordUrl for the full story.
    onLetter?.(tk.indices[0], tk.letter);
    try {
      const doubleUrl = !prefetchFailed ? _preloadB64(_DOUBLE_WORD_KEY, null) : null;
      if (doubleUrl) await _playUrl(doubleUrl);
      else await browserSpeak("Double", 0.9);
    } catch {
      await browserSpeak("Double", 0.9);
    }
    if (shouldStop?.()) return;
    // Brief gap so "Double" and the letter don't run together.
    await new Promise((r) => setTimeout(r, DOUBLE_INNER_GAP_MS));
    if (shouldStop?.()) return;
    // Phase 2: play the single letter while highlighting the second
    // letter. The handoff happens at the natural boundary between words.
    onLetter?.(tk.indices[1], tk.letter);
    try {
      const letterUrl = !prefetchFailed ? _preloadB64(_letterKey(tk.letter), null) : null;
      if (letterUrl) await _playUrl(letterUrl);
      else await browserSpeak(tk.letter, 0.85);
    } catch {
      await browserSpeak(tk.letter, 0.85);
    }
  };

  for (const tk of tokens) {
    if (shouldStop?.()) { onLetter?.(-1, null); return "stopped"; }
    if (tk.type === "single") await playSingle(tk);
    else await playDouble(tk);
    if (shouldStop?.()) { onLetter?.(-1, null); return "stopped"; }
    await new Promise((r) => setTimeout(r, INTER_TOKEN_GAP_MS));
  }
  onLetter?.(-1, null);
  if (shouldStop?.()) return "stopped";
  if (skipFinalWord) return prefetchFailed ? "browser" : "server";
  await new Promise((r) => setTimeout(r, 220));
  if (shouldStop?.()) return "stopped";
  await playWord(word);
  return prefetchFailed ? "browser" : "server";
};
// Re-export of the unlock function for places that want the new name.
export { _unlockAudio as unlockAudio };
